import React, { useEffect, useState, useCallback } from "react";
import Shell from "@/components/app/Shell";
import RequestCard from "@/components/app/RequestCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import CloseRequestButton from "@/components/app/CloseRequestButton";
import api, { formatApiError } from "@/lib/apiClient";
import { techWhatsAppLink } from "@/lib/status";
import EditStaffDialog from "@/components/app/EditStaffDialog";
import ResetPasswordDialog from "@/components/app/ResetPasswordDialog";
import ContractsTab from "@/components/app/ContractsTab";
import QrBankTab from "@/components/app/QrBankTab";
import StaffEquipmentTab from "@/components/app/StaffEquipmentTab";
import CustomersTab from "@/components/app/CustomersTab";
import ServiceCatalogTab from "@/components/app/ServiceCatalogTab";
import CreateCustomerDialog from "@/components/app/CreateCustomerDialog";
import ServiceOrderDialog from "@/components/app/ServiceOrderDialog";
import AgendaView from "@/components/app/AgendaView";
import FinancialPanel from "@/components/app/FinancialPanel";
import { toast } from "sonner";
import { MessageCircle, UserPlus, Users, Pencil, KeyRound, FileText, RotateCcw } from "lucide-react";

function AssignControl({ request, technicians, onChanged }) {
  const [techId, setTechId] = useState(request.assigned_technician_id || "");
  const [busy, setBusy] = useState(false);

  const assign = async () => {
    if (!techId) { toast.error("Selecione um técnico."); return; }
    setBusy(true);
    try {
      await api.patch(`/requests/${request.id}/assign`, { technician_id: techId });
      toast.success("Técnico atribuído!");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const done = ["concluido", "cancelado"].includes(request.status);

  const reopen = async () => {
    try {
      await api.patch(`/requests/${request.id}/reopen`);
      toast.success("Chamado reaberto.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-2">
      {request.status === "cancelado" && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="outline" className="w-full gap-1.5 border-blue-500 text-blue-700 hover:bg-blue-50"
              data-testid={`fr-reopen-button-${request.id}`}>
              <RotateCcw className="h-4 w-4" /> Reabrir chamado
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reabrir chamado?</AlertDialogTitle>
              <AlertDialogDescription>O chamado voltará a ficar ativo e o cliente será notificado.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Voltar</AlertDialogCancel>
              <AlertDialogAction className="bg-blue-600 hover:bg-blue-700" onClick={reopen}
                data-testid={`fr-reopen-yes-${request.id}`}>Sim, reabrir</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {!done && (
        <div className="flex gap-2">
          <Select value={techId} onValueChange={setTechId}>
            <SelectTrigger className="h-9" data-testid={`fr-assign-select-${request.id}`}>
              <SelectValue placeholder={technicians.length ? "Atribuir técnico" : "Cadastre um técnico primeiro"} />
            </SelectTrigger>
            <SelectContent>
              {technicians.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`fr-tech-option-${t.id}`}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-9 bg-blue-600 hover:bg-blue-700" onClick={assign}
            disabled={busy || !technicians.length} data-testid={`fr-assign-button-${request.id}`}>
            {request.assigned_technician_id ? "Reatribuir" : "Atribuir"}
          </Button>
        </div>
      )}
      {request.assigned_technician_id && (
        <a href={techWhatsAppLink(request)} target="_blank" rel="noopener noreferrer"
          data-testid={`fr-whatsapp-notify-${request.id}`}>
          <Button size="sm" variant="outline" className="w-full gap-1.5 border-green-500 text-green-700 hover:bg-green-50">
            <MessageCircle className="h-4 w-4" /> Notificar técnico no WhatsApp
          </Button>
        </a>
      )}
      {!done && <CloseRequestButton request={request} onChanged={onChanged} testPrefix="fr" />}
    </div>
  );
}

function CreateTechDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "technician" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("Técnico cadastrado!");
      setForm({ name: "", email: "", phone: "", password: "", role: "technician" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="fr-add-tech-button">
          <UserPlus className="h-4 w-4" /> Adicionar técnico
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="fr-create-tech-dialog">
        <DialogHeader><DialogTitle>Novo técnico da equipe</DialogTitle>
          <DialogDescription>O técnico ficará vinculado à sua franquia.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Nome</Label>
            <Input required value={form.name} onChange={upd("name")} data-testid="fr-tech-name-input" /></div>
          <div className="space-y-1.5"><Label>E-mail</Label>
            <Input type="email" required value={form.email} onChange={upd("email")} data-testid="fr-tech-email-input" /></div>
          <div className="space-y-1.5"><Label>Telefone / WhatsApp</Label>
            <Input required value={form.phone} onChange={upd("phone")} placeholder="(21) 99999-9999" data-testid="fr-tech-phone-input" /></div>
          <div className="space-y-1.5"><Label>Senha</Label>
            <Input type="password" required value={form.password} onChange={upd("password")} placeholder="Mínimo 6 caracteres" data-testid="fr-tech-password-input" /></div>
          <DialogFooter>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="fr-submit-tech-button">
              {busy ? "Salvando..." : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const FILTERS = [
  { v: "", label: "Todos" },
  { v: "aberto", label: "Abertos" },
  { v: "atribuido", label: "Atribuídos" },
  { v: "a_caminho", label: "A caminho" },
  { v: "concluido", label: "Concluídos" },
];

export default function FranchiseeDashboard() {
  const [requests, setRequests] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [editMember, setEditMember] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [osReq, setOsReq] = useState(null);

  const loadRequests = useCallback(async () => {
    const { data } = await api.get("/requests", { params: filter ? { status: filter } : {} });
    setRequests(data);
  }, [filter]);

  const loadTeam = useCallback(async () => {
    const { data } = await api.get("/users", { params: { role: "technician" } });
    setTechnicians(data);
  }, []);

  useEffect(() => {
    (async () => { setLoading(true); await Promise.all([loadRequests(), loadTeam()]); setLoading(false); })();
  }, [loadRequests, loadTeam]);

  const resetPassword = async (member) => {
    try {
      const { data } = await api.post(`/users/${member.id}/reset-password`);
      setResetResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const slaCount = requests.filter((r) => r.sla_breached).length;

  return (
    <Shell title="Painel do Franqueado" subtitle="Chamados da sua região e sua equipe"
      actions={<CreateCustomerDialog />}>
      <Tabs defaultValue="chamados">
        <TabsList className="mb-4">
          <TabsTrigger value="chamados" data-testid="fr-tab-chamados">
            Chamados{slaCount > 0 && <Badge className="ml-2 bg-red-100 text-red-700 border-red-200" data-testid="fr-sla-badge">{slaCount} SLA</Badge>}
          </TabsTrigger>
          <TabsTrigger value="agenda" data-testid="fr-tab-agenda">Agenda</TabsTrigger>
          <TabsTrigger value="financeiro" data-testid="fr-tab-financeiro">Financeiro</TabsTrigger>
          <TabsTrigger value="equipe" data-testid="fr-tab-equipe">Minha equipe</TabsTrigger>
          <TabsTrigger value="contratos" data-testid="fr-tab-contratos">Contratos</TabsTrigger>
          <TabsTrigger value="qrcodes" data-testid="fr-tab-qrcodes">QR Codes</TabsTrigger>
          <TabsTrigger value="equipamentos" data-testid="fr-tab-equipamentos">Equipamentos</TabsTrigger>
          <TabsTrigger value="clientes" data-testid="fr-tab-clientes">Clientes</TabsTrigger>
          <TabsTrigger value="servicos" data-testid="fr-tab-servicos">Serviços</TabsTrigger>
        </TabsList>

        <TabsContent value="chamados">
          <div className="flex gap-2 flex-wrap mb-4">
            {FILTERS.map((f) => (
              <Button key={f.v} size="sm" variant={filter === f.v ? "default" : "outline"}
                className={filter === f.v ? "bg-blue-600 hover:bg-blue-700" : ""}
                onClick={() => setFilter(f.v)} data-testid={`fr-filter-${f.v || "all"}`}>
                {f.label}
              </Button>
            ))}
          </div>
          {loading ? (
            <p className="text-slate-400 text-sm">Carregando...</p>
          ) : requests.length === 0 ? (
            <p className="text-slate-500 py-10 text-center" data-testid="fr-empty-requests">Nenhum chamado na sua região ainda.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2" data-testid="fr-requests-list">
              {requests.map((r) => (
                <RequestCard key={r.id} request={r} showCustomer
                  actions={
                    <div className="space-y-2">
                      <AssignControl request={r} technicians={technicians} onChanged={loadRequests} />
                      <Button size="sm" variant="outline" className="w-full gap-1.5 border-[#1e7be0] text-[#1e7be0] hover:bg-blue-50"
                        onClick={() => setOsReq(r)} data-testid={`service-order-${r.id}`}>
                        <FileText className="h-4 w-4" /> Ordem de Serviço
                      </Button>
                    </div>
                  } />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="agenda">
          <AgendaView requests={requests} technicians={technicians} showTechFilter
            onReschedule={async (id, date, time) => {
              try { await api.patch(`/requests/${id}/schedule`, { scheduled_date: date, scheduled_time: time }); toast.success("Chamado reagendado."); loadRequests(); }
              catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Falha ao reagendar."); }
            }} />
        </TabsContent>

        <TabsContent value="financeiro">
          <FinancialPanel />
        </TabsContent>

        <TabsContent value="equipe">
          <div className="flex justify-end mb-4">
            <CreateTechDialog onCreated={loadTeam} />
          </div>
          {technicians.length === 0 ? (
            <p className="text-slate-500 py-10 text-center" data-testid="fr-empty-team">Nenhum técnico cadastrado.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="fr-team-list">
              {technicians.map((u) => (
                <Card key={u.id} className="border-slate-200" data-testid={`fr-team-member-${u.id}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <Users className="h-5 w-5 text-slate-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-800 truncate">{u.name}</p>
                        <p className="text-xs text-slate-500 truncate">{u.email} · {u.phone || "-"}</p>
                      </div>
                      <Badge variant="secondary">Técnico</Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1 gap-1.5"
                        onClick={() => setEditMember(u)} data-testid={`fr-edit-member-${u.id}`}>
                        <Pencil className="h-4 w-4" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50"
                        onClick={() => resetPassword(u)} data-testid={`fr-reset-password-${u.id}`}>
                        <KeyRound className="h-4 w-4" /> Senha provisória
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="contratos">
          <ContractsTab canCreate />
        </TabsContent>

        <TabsContent value="qrcodes">
          <QrBankTab />
        </TabsContent>

        <TabsContent value="equipamentos">
          <StaffEquipmentTab />
        </TabsContent>

        <TabsContent value="clientes">
          <CustomersTab />
        </TabsContent>

        <TabsContent value="servicos">
          <ServiceCatalogTab />
        </TabsContent>
      </Tabs>

      <EditStaffDialog open={!!editMember} onOpenChange={(o) => !o && setEditMember(null)}
        member={editMember} onSaved={loadTeam} />
      <ResetPasswordDialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}
        result={resetResult} />
      <ServiceOrderDialog request={osReq} open={!!osReq} onOpenChange={(o) => !o && setOsReq(null)}
        onSaved={loadRequests} />
    </Shell>
  );
}
